import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { FaCar, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  padding: ${theme.spacing.md};
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xxl};
  width: 100%;
  max-width: 400px;
  box-shadow: ${theme.shadows.lg};
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const LogoText = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${theme.colors.mediumGray};
  font-size: 1rem;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.lightGray};
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: ${theme.spacing.md};
  border: none;
  background: ${props => props.active ? theme.colors.primary : 'transparent'};
  color: ${props => props.active ? theme.colors.white : theme.colors.mediumGray};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${theme.colors.secondary};
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const PasswordContainer = styled.div`
  position: relative;
`;

const PasswordInput = styled(Input)`
  padding-right: 3rem;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.mediumGray};
  cursor: pointer;
  padding: ${theme.spacing.sm};
`;

const PasswordRequirements = styled.div`
  background: ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
`;

const RequirementsTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.sm};
  font-size: 0.9rem;
`;

const RequirementItem = styled.div<{ valid: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 0.8rem;
  color: ${props => props.valid ? theme.colors.success : theme.colors.mediumGray};
  margin-bottom: ${theme.spacing.xs};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RequirementIcon = styled.div<{ valid: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.valid ? theme.colors.success : theme.colors.lightGray};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: ${theme.colors.white};
  font-weight: bold;
`;

const Button = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: ${theme.spacing.md};
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: ${theme.colors.mediumGray};
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: 0.9rem;
  text-align: center;
  margin-top: ${theme.spacing.sm};
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid ${theme.colors.white};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: ''
  });

  const validatePassword = (password: string) => {
    return {
      minLength: password.length >= 8,
      maxLength: password.length <= 4096,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumeric: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  };

  const passwordValidation = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const { login, register } = useAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const success = await login(formData.email, formData.password);
        if (!success) {
          setError('Invalid email or password');
        }
      } else {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!isPasswordValid) {
          setError('Password does not meet requirements');
          setLoading(false);
          return;
        }
        const success = await register(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName
        );
        if (!success) {
          setError('Registration failed. Please try again.');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Logo>
          <FaCar style={{ fontSize: '3rem', color: theme.colors.primary, marginBottom: '1rem' }} />
          <LogoText>971 RUN</LogoText>
          <Subtitle>Premium Car Wash Services</Subtitle>
        </Logo>

        <TabContainer>
          <Tab active={isLogin} onClick={() => setIsLogin(true)}>
            Sign In
          </Tab>
          <Tab active={!isLogin} onClick={() => setIsLogin(false)}>
            Sign Up
          </Tab>
        </TabContainer>

        <Form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <InputGroup>
                <Label>First Name</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </InputGroup>
              <InputGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </InputGroup>
            </>
          )}

          <InputGroup>
            <Label>Email</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>Password</Label>
            <PasswordContainer>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </PasswordToggle>
            </PasswordContainer>
            {!isLogin && formData.password && (
              <PasswordRequirements>
                <RequirementsTitle>Password Requirements:</RequirementsTitle>
                <RequirementItem valid={passwordValidation.minLength}>
                  <RequirementIcon valid={passwordValidation.minLength}>✓</RequirementIcon>
                  At least 8 characters
                </RequirementItem>
                <RequirementItem valid={passwordValidation.maxLength}>
                  <RequirementIcon valid={passwordValidation.maxLength}>✓</RequirementIcon>
                  Maximum 4096 characters
                </RequirementItem>
                <RequirementItem valid={passwordValidation.hasUppercase}>
                  <RequirementIcon valid={passwordValidation.hasUppercase}>✓</RequirementIcon>
                  One uppercase letter (A-Z)
                </RequirementItem>
                <RequirementItem valid={passwordValidation.hasLowercase}>
                  <RequirementIcon valid={passwordValidation.hasLowercase}>✓</RequirementIcon>
                  One lowercase letter (a-z)
                </RequirementItem>
                <RequirementItem valid={passwordValidation.hasNumeric}>
                  <RequirementIcon valid={passwordValidation.hasNumeric}>✓</RequirementIcon>
                  One number (0-9)
                </RequirementItem>
                <RequirementItem valid={passwordValidation.hasSpecial}>
                  <RequirementIcon valid={passwordValidation.hasSpecial}>✓</RequirementIcon>
                  One special character (!@#$%^&*...)
                </RequirementItem>
              </PasswordRequirements>
            )}
          </InputGroup>

          {!isLogin && (
            <InputGroup>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
              />
            </InputGroup>
          )}

          <Button type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : (isLogin ? 'Sign In' : 'Sign Up')}
          </Button>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>
      </Card>
    </Container>
  );
};

export default AuthPage;

